/**
 * Shared file-intake helpers for the universal drag & drop work:
 *
 *  - useFileDrop     — drop-target props + dragging state for any element
 *  - usePasteFiles   — clipboard paste-to-upload (screenshots) while mounted
 *  - useWindowFileDrag — "is a file being dragged over the window?" for
 *    whole-page overlay drop zones
 *
 * All of them only react to real OS file drags (dataTransfer.types includes
 * "Files") so in-app drags (dispatch cards, etc.) never trigger overlays.
 */
import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";

export function dragHasFiles(e: { dataTransfer: DataTransfer | null }): boolean {
    return !!e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files");
}

/** Drop-target props for a single element (dialog body, upload tile, …). */
export function useFileDrop(onFiles: (files: File[]) => void) {
    const [dragging, setDragging] = useState(false);
    const onDrop = useCallback(
        (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setDragging(false);
            const files = Array.from(e.dataTransfer?.files ?? []);
            if (files.length > 0) onFiles(files);
        },
        [onFiles],
    );
    const dropProps = {
        onDragOver: (e: DragEvent) => {
            if (!dragHasFiles(e)) return;
            e.preventDefault();
            setDragging(true);
        },
        onDragLeave: (e: DragEvent) => {
            e.preventDefault();
            setDragging(false);
        },
        onDrop,
    };
    return { dragging, dropProps };
}

/**
 * Paste-from-clipboard while the owning surface is open. Screenshots and
 * copied images arrive as clipboardData.files.
 */
export function usePasteFiles(onFiles: (files: File[]) => void, enabled = true) {
    const handler = useRef(onFiles);
    handler.current = onFiles;
    useEffect(() => {
        if (!enabled) return;
        const onPaste = (e: ClipboardEvent) => {
            const files = Array.from(e.clipboardData?.files ?? []);
            if (files.length === 0) return;
            // Don't hijack text pastes into inputs — only act when the
            // clipboard actually carries files.
            e.preventDefault();
            handler.current(files);
        };
        window.addEventListener("paste", onPaste);
        return () => window.removeEventListener("paste", onPaste);
    }, [enabled]);
}

/**
 * Window-level "file drag in progress" tracker for whole-page drop zones.
 * Uses a depth counter (dragenter/dragleave fire per element) and resets on
 * drop or when the drag leaves the window.
 */
export function useWindowFileDrag(): boolean {
    const [active, setActive] = useState(false);
    const depth = useRef(0);
    useEffect(() => {
        const enter = (e: globalThis.DragEvent) => {
            if (!dragHasFiles(e)) return;
            depth.current += 1;
            setActive(true);
        };
        const leave = () => {
            depth.current = Math.max(0, depth.current - 1);
            if (depth.current === 0) setActive(false);
        };
        const drop = () => {
            depth.current = 0;
            setActive(false);
        };
        const over = (e: globalThis.DragEvent) => {
            // Required so the browser doesn't navigate to the dropped file
            // when it misses our overlay.
            if (dragHasFiles(e)) e.preventDefault();
        };
        window.addEventListener("dragenter", enter);
        window.addEventListener("dragleave", leave);
        window.addEventListener("drop", drop);
        window.addEventListener("dragover", over);
        return () => {
            window.removeEventListener("dragenter", enter);
            window.removeEventListener("dragleave", leave);
            window.removeEventListener("drop", drop);
            window.removeEventListener("dragover", over);
        };
    }, []);
    return active;
}

export function isPdfFile(f: File): boolean {
    return f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
}

/**
 * A PDF saved without ".pdf" ("Passed Serial No 261277 pdf" — a space where the
 * dot belongs) has no mime type and no extension, so every name-based filter
 * above calls it "not a PDF": City Filing refuses it and the page drop sends it
 * to paperwork as a random document. Read the first 1 KB; when it opens with the
 * PDF header, hand back a copy named "….pdf" with type application/pdf so every
 * downstream check (and the server) sees it for what it is. Anything else —
 * images, real PDFs, unreadable files — comes back untouched.
 */
export async function normalizePdfFiles(files: File[]): Promise<File[]> {
    return Promise.all(
        files.map(async (f) => {
            if (isPdfFile(f) || f.type.startsWith("image/")) return f;
            try {
                const head = new Uint8Array(await f.slice(0, 1024).arrayBuffer());
                if (!new TextDecoder("latin1").decode(head).includes("%PDF-")) return f;
            } catch {
                return f;
            }
            return new File([f], pdfFileName(f.name), { type: "application/pdf", lastModified: f.lastModified });
        }),
    );
}

/** Same rule as the server's `pdfFileName`: "x pdf" / "x_pdf" → "x.pdf", else append ".pdf". */
export function pdfFileName(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) return "document.pdf";
    if (/\.pdf$/i.test(trimmed)) return trimmed;
    const stripped = trimmed.replace(/[\s_.-]+pdf$/i, "");
    return `${stripped || trimmed}.pdf`;
}

export function isImageFile(f: File): boolean {
    return f.type.startsWith("image/") || /\.(heic|heif|png|jpe?g|webp|gif)$/i.test(f.name);
}
